import Paginator from 'types/interfaces/Paginator';
import { GlobalVariables } from 'config/constant';
import { BaseQueryApi, QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { RootState } from 'redux/store';
import {
  GetRefreshTokenStore,
  setAccessToken,
  setLogOut,
  updateRefreshToken,
} from 'redux/slices/Auth/authSlice';
import { ENDPOINTS } from 'config/constant/endpoints.config';
import { baseQuery } from 'redux/api/BaseQueryConfig';
import { Mutex } from 'async-mutex';
import { FilterEnum } from 'types/interfaces/filter.enum';
import { BooleanToDom, ClientStatusEnum } from 'config/enums/common.enums';
import {
  DealStatesEnum,
  ExpandEnum,
  Nillable,
  ProductPriceTypeEnum,
  ProductTypeEnum,
} from 'types/interfaces/sharedType.type';
import { isArray, uniq } from 'lodash';
import { DiscountTypeEnum } from 'types/enum/discount-type.enum';
import { BooleanString } from 'types/interfaces/BooleanString';
import { buildArgsWithApiKey } from 'utils/helpers/api-key-request.helpers';

export function formatArrayToQueryString(numbers: Nillable<number[]>): Nillable<string> {
  return numbers ? uniq(numbers).join(',') : null;
}
export function injectFilter<T>(propertyName: FilterEnum, propertyValue: T | null | undefined) {
  if (!propertyValue) return {};
  return {
    [propertyName]: propertyValue,
  };
}
function injectArrayFilter<T>(arrayToInject: T[] | null | undefined, propertyName: FilterEnum) {
  if (!arrayToInject || arrayToInject.length === 0) return undefined;
  const array: Record<string, T>[] = [];
  arrayToInject.map((item) => array.push(injectFilter<T>(propertyName, item)));
  return { [propertyName]: array };
}
export const paginatorToApiPagination = (paginator: Paginator | undefined) => {
  if (paginator) {
    return {
      ...paginator,
      itemsPerPage: paginator.rowsPerPage,
      order: paginator.order,
      keyword: paginator.searchText,
    };
  }
  return {};
};
export const injectFilterStatus = (query: boolean | null) => {
  if (query === null) return undefined;
  const result = query ? GlobalVariables.STATUS.ACTIVE : GlobalVariables.STATUS.INACTIVE;
  return injectFilter(FilterEnum.Status, result);
};
export const injectActivationStatusClientFilter = (query: boolean | null) => {
  if (query === null) return undefined;
  const result = query ? GlobalVariables.STATUS.ACTIVE : GlobalVariables.STATUS.INACTIVE;
  return injectFilter(FilterEnum.ActivationStatus, result);
};
export const injectActivationStatusFilter = (query: string | undefined | null) => {
  if (query === null) return undefined;

  return injectFilter(FilterEnum.Status, query);
};
export const injectFilterDate = (query: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.Date, query);
};
export const injectWarehouseScopeIds = (
  warehouseScopeIds: Nillable<number[]>,
  shouldFormatToString?: boolean,
) =>
  shouldFormatToString
    ? injectFilter<string>(
        FilterEnum.WarehouseScopeIds,
        formatArrayToQueryString(warehouseScopeIds),
      )
    : injectArrayFilter<number>(warehouseScopeIds, FilterEnum.WarehouseScopeIds);

export const injectFilterWarehouseIds = (query: Nillable<number[]> | Nillable<number>) =>
  isArray(query)
    ? injectArrayFilter<number>(query, FilterEnum.WarehouseIds)
    : injectFilter<number>(FilterEnum.WarehouseIds, query);

export const injectFilterAgentId = (query: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.AgentId, query);
};
export const injectFilterOriginOrderId = (query: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.OriginOrderId, query);
};
export const injectFilterProfileId = (query: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.Profile, query);
};
export const injectFilterClientId = (query: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.ClientId, query);
};
export const injectFilterUserId = (query: Nillable<number>) =>
  injectFilter<number>(FilterEnum.UserId, query);
export const injectFilterHasSupportArticle = (query: 'true' | 'false' | null | undefined) =>
  injectFilter<'true' | 'false'>(FilterEnum.HasSupport, query);

export const injectFilterHasDiscountArticle = (query: 'true' | 'false' | null | undefined) =>
  injectFilter<'true' | 'false'>(FilterEnum.HasDiscount, query);

export const injectFilterSectorId = (
  sectorId: number | null | undefined | number[],
  customProp?: FilterEnum,
  shouldFormatToString?: boolean,
) => {
  if (shouldFormatToString && Array.isArray(sectorId)) {
    return injectFilter<string>(
      customProp ?? FilterEnum.Sector,
      formatArrayToQueryString(sectorId),
    );
  }
  if (Array.isArray(sectorId)) {
    return injectArrayFilter<number>(sectorId, customProp ?? FilterEnum.Sector);
  }
  return injectFilter<number>(customProp ?? FilterEnum.Sector, sectorId);
};
export const injectFilterClientCategoryId = (clientCategoryId: number | null | undefined) =>
  injectFilter<number>(FilterEnum.ClientCategory, clientCategoryId);

export const injectFilterClientCategoryIdArray = (
  clientCategoryId: number[] | null | undefined,
  customProp?: FilterEnum,
) => injectArrayFilter<number>(clientCategoryId, customProp ?? FilterEnum.CategoryId);

export const injectFilterClientClassId = (clientClassId: number | null | undefined) =>
  injectFilter<number>(FilterEnum.ClientClass, clientClassId);

export const injectFilterClientClassArray = (
  clientClassId: Nillable<number[]>,
  customProp?: FilterEnum,
  shouldFormatToString?: boolean,
) => {
  if (shouldFormatToString && Array.isArray(clientClassId))
    return injectFilter<string>(
      customProp ?? FilterEnum.ClassId,
      formatArrayToQueryString(clientClassId),
    );
  if (Array.isArray(clientClassId)) {
    return injectArrayFilter<number>(clientClassId, customProp ?? FilterEnum.ClassId);
  }
  return injectFilter<number>(customProp ?? FilterEnum.ClassId, clientClassId);
};
export const injectFilterIds = (ids: Nillable<number[]>) =>
  injectArrayFilter<number>(ids, FilterEnum.Ids);
export const injectFilterCategoryId = (categoryId: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.Category, categoryId);
};
export const injectFilterZoneId = (
  zoneId: Nillable<number | number[]>,
  shouldFormatToString?: boolean,
) =>
  shouldFormatToString && isArray(zoneId)
    ? injectFilter<string>(FilterEnum.Zone, formatArrayToQueryString(zoneId))
    : isArray(zoneId)
    ? injectArrayFilter<number>(zoneId, FilterEnum.Zone)
    : injectFilter<number>(FilterEnum.Zone, zoneId);
export const injectFilterZoneIds = (
  zoneIds: number[] | null | undefined,
  shouldFormatToString?: boolean,
) =>
  shouldFormatToString
    ? injectFilter<string>(FilterEnum.Zones, formatArrayToQueryString(zoneIds))
    : injectArrayFilter<number>(zoneIds, FilterEnum.Zones);

export const injectFilterProductType = (productType: ProductTypeEnum | null | undefined) =>
  injectFilter<ProductTypeEnum>(FilterEnum.ProductType, productType);

export const injectZoneChiefId = (zoneChiefId: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.ZoneChief, zoneChiefId);
};
export const injectFilterProductPriceType = (
  productPriceType: ProductPriceTypeEnum | null | undefined,
) => injectFilter<ProductPriceTypeEnum>(FilterEnum.ProductPriceType, productPriceType);

export const injectFilterProductCatalog = (catalogId: number | number[] | null | undefined) =>
  isArray(catalogId)
    ? injectArrayFilter<number>(catalogId, FilterEnum.ProductCatalog)
    : injectFilter<number>(FilterEnum.ProductCatalog, catalogId);

export const injectFilterProductBrand = (brand: number | null | undefined) => {
  return injectFilter<number>(FilterEnum.ProductBrand, brand);
};
export const injectExpand = (expand?: ExpandEnum[] | null) =>
  injectArrayFilter<ExpandEnum>(expand, FilterEnum.Expand);
export const injectDiffs = (value?: BooleanToDom | null) => injectFilter(FilterEnum.Diffs, value);
export const injectFilterWarehouseId = (warehouseId: number | null | undefined) =>
  injectFilter<number>(FilterEnum.WarehouseId, warehouseId);
export const injectFilterSalesLineId = (salesLineId: number | null | undefined) =>
  injectFilter<number>(FilterEnum.SalesLineId, salesLineId);
export const injectClientStatusFilter = (query: ClientStatusEnum | undefined | null) =>
  injectFilter<ClientStatusEnum>(FilterEnum.ClientStatus, query);
export const injectDealStatesFilter = (query: DealStatesEnum[] | undefined | null) =>
  injectArrayFilter<DealStatesEnum>(query, FilterEnum.DealStates);
export const injectExcludedDiscountsFilter = (query: Nillable<number[]>) =>
  injectArrayFilter<number>(query, FilterEnum.ExcludedDiscounts);

export const injectFilterClientsArray = (
  clientIds: Nillable<number[]>,
  customProp?: FilterEnum,
  shouldFormatToString?: boolean,
) =>
  shouldFormatToString
    ? injectFilter<string>(customProp ?? FilterEnum.ClientIds, formatArrayToQueryString(clientIds))
    : injectArrayFilter<number>(clientIds, customProp ?? FilterEnum.ClientIds);

export const injectFilterStockClients = (array: Nillable<number[]>) => {
  return injectArrayFilter<number>(array, FilterEnum.StockClients);
};
export const injectFilterExcludeDiscountType = (query: Nillable<DiscountTypeEnum>) =>
  injectFilter<DiscountTypeEnum>(FilterEnum.ExcludeDiscountType, query);

export const injectEnableFilterByClientsScopeForWarehouse = (query: Nillable<boolean>) =>
  injectFilter<boolean>(FilterEnum.EnableFilterByClientsScopeForWarehouse, query);
export const injectFilterArticleByWarehouses = (
  query: Nillable<number[]>,
  shouldFormatToString?: boolean,
) =>
  shouldFormatToString
    ? injectFilter<string>(FilterEnum.WarehouseIds, formatArrayToQueryString(query))
    : injectArrayFilter<number>(query, FilterEnum.WarehouseIds);
export const injectShouldDisableFilterStock = (query: Nillable<BooleanString>) =>
  injectFilter<BooleanString>(FilterEnum.ShouldDisableFilterStock, query);

export async function handleRefreshToken(
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>,
  args: string | FetchArgs,
  mutex: Mutex,
) {
  const fetchWithBaseQuery = fetchBaseQuery(baseQuery);

  const release = await mutex.acquire();
  try {
    const state = api.getState() as RootState;
    const refreshToken = GetRefreshTokenStore(state);

    const refreshRequestArgs = await buildArgsWithApiKey(
      {
        method: 'POST',
        url: ENDPOINTS.REFRESH_TOKEN,
        body: { refreshToken },
      },
      'POST',
    );

    const refreshResult = await fetchWithBaseQuery(refreshRequestArgs, api, extraOptions);
    if (refreshResult.data) {
      api.dispatch(
        setAccessToken({
          token: (refreshResult.data as { accessToken: string }).accessToken,
        }),
      );
      if ((refreshResult.data as { refreshToken: string }).refreshToken)
        api.dispatch(
          updateRefreshToken({
            refreshToken: (refreshResult.data as { refreshToken: string }).refreshToken,
          }),
        );
      // retry the initial query
      const retryArgs = await buildArgsWithApiKey(args);
      result = await fetchWithBaseQuery(retryArgs, api, extraOptions);
    } else {
      api.dispatch(setLogOut());
    }
  } finally {
    // release must be called once the mutex should be released again.
    release();
  }
  return result;
}
